<template>
    <template v-if="to">
        <Button v-slot="slotProps" v-bind="mappedProps" as-child>
            <router-link :to="to" :class="slotProps.class" :style="linkStyle">
                <slot>{{ $attrs.label ?? slotText }}</slot>
            </router-link>
        </Button>
    </template>

    <Button v-else v-bind="mappedProps" :label="$attrs.label ?? slotText">
        <slot v-if="!hasIcon" />
    </Button>
</template>

<script>
import { getPrimeVueSeverity } from './utils/componentMappings.js';

/**
 * EButton - Abstraction wrapper around PrimeVue's Button
 * class="[&:not(:has(i))]:tw:px-8"
 * This component provides a unified API that works with PrimeVue.
 * It automatically maps Buefy props to their PrimeVue equivalents.
 *
 * Usage:
 *   <e-button type="is-primary" @click="handleClick">Click Me</e-button>
 *   <e-button type="is-danger" outlined>Delete</e-button>
 *   <e-button type="is-success" size="is-large" loading>Save</e-button>
 *   <e-button icon-left="plus" icon-right="check">With Icons</e-button>
 *   <e-button type="is-primary" :to="{ name: 'route-name' }">Router Link</e-button>
 *
 * Buefy Props (work in both modes):
 *   - type: is-primary, is-success, is-danger, is-warning, is-info, is-link, is-dark, is-light
 *   - size: is-small, is-medium, is-large
 *   - outlined: Boolean (shows as outlined/ghost button)
 *   - rounded: Boolean (rounded corners)
 *   - loading: Boolean (shows loading spinner)
 *   - disabled: Boolean (disables button)
 *   - icon-left: String (icon name for left side)
 *   - icon-right: String (icon name for right side)
 *   - expanded: Boolean (makes button full width)
 *   - native-type: String (HTML button type: button, submit, reset)
 *   - to: String|Object (Vue Router destination, renders as router-link)
 *
 * PrimeVue-specific props:
 *   - text: Boolean (text-only button)
 *   - raised: Boolean (adds shadow)
 *   - plain: Boolean (plain text button)
 *
 * Property Mappings (Buefy → PrimeVue):
 *   - type → severity
 *   - size → size
 *   - icon-left → icon
 *   - icon-right → icon + iconPos="right"
 *   - expanded → adds 'w-full' class
 *   - native-type → type
 */
const PRIMEVUE_BUTTON_SIZE_MAP = {
    'is-small': 'small',
    'is-medium': undefined,
    'is-large': 'large',
    small: 'small',
    medium: undefined,
    large: 'large',
};

export default {
    name: 'EButton',
    inheritAttrs: false,
    props: {
        to: {
            type: [String, Object],
            default: null,
        },
    },
    computed: {
        mappedProps() {
            const attrs = { ...this.$attrs };
            const mapped = {};

            if (attrs.class) {
                const { severity, filteredClasses } = this.extractSeverityFromClasses(attrs.class);
                if (severity && !attrs.type) {
                    mapped.severity = severity;
                }
                attrs.class = filteredClasses;
            }

            if (attrs.type) {
                if (attrs.type === 'is-text') {
                    mapped.variant = 'text';
                    mapped.severity = 'contrast';
                } else {
                    mapped.severity = getPrimeVueSeverity(attrs.type);
                }
                delete attrs.type;
            } else {
                mapped.severity = getPrimeVueSeverity();
            }

            if (attrs.size) {
                const size = this.getPrimeVueButtonSize(attrs.size);
                if (size !== undefined) {
                    mapped.size = size;
                }
                delete attrs.size;
            }

            if (attrs['icon-left']) {
                const hasPrefix = ['pi', 'uil'].some(prefix => attrs['icon-left'].includes(prefix));

                mapped.icon = hasPrefix ? attrs['icon-left'] : `uil uil-${attrs['icon-left']}`;
                delete attrs['icon-left'];
            }

            if (attrs['icon-right']) {
                const hasPrefix = ['pi', 'uil'].some(prefix => attrs['icon-right'].includes(prefix));

                mapped.icon = hasPrefix ? attrs['icon-right'] : `uil uil-${attrs['icon-right']}`;
                mapped.iconPos = 'right';
                delete attrs['icon-right'];
            }

            // Handle icon from slot (if no attribute icons were found)
            if (!mapped.icon && this.slotIconInfo) {
                mapped.icon = this.slotIconInfo.icon;
                if (this.slotIconInfo.position === 'right') {
                    mapped.iconPos = 'right';
                }
            }

            if (attrs.expanded) {
                mapped.class = [attrs.class, 'tw:w-full'].filter(Boolean).join(' ');
                delete attrs.expanded;
            }

            if (attrs['native-type']) {
                mapped.type = attrs['native-type'];
                delete attrs['native-type'];
            }

            return { ...attrs, ...mapped };
        },
        linkStyle() {
            const severity = this.mappedProps.severity || 'primary';
            return {
                backgroundColor: `var(--p-button-${severity}-background)`,
                color: `var(--p-button-${severity}-color)`,
            };
        },
        hasAttributeIcon() {
            return !!this.$attrs['icon-left'] || !!this.$attrs['icon-right'];
        },
        hasIcon() {
            return this.hasAttributeIcon || this.hasSlotIcon;
        },
        hasSlotIcon() {
            if (!this.$slots.default) return false;

            const slots = this.$slots.default();

            // Check if slot contains an icon component (e-icon, b-icon, or i element)
            const hasIconInSlot = nodes => {
                const nodeArray = Array.isArray(nodes) ? nodes : [nodes];

                return nodeArray.some(node => {
                    if (!node) return false;

                    // Check if it's an icon component
                    if (node.type && typeof node.type === 'object') {
                        const componentName = node.type.name || node.type.__name;
                        if (componentName === 'EIcon' || componentName === 'BIcon') {
                            return true;
                        }
                    }

                    // Check if it's an <i> element with icon classes
                    if (node.type === 'i' && node.props && node.props.class) {
                        let classes = '';
                        if (typeof node.props.class === 'string') {
                            classes = node.props.class;
                        } else if (Array.isArray(node.props.class)) {
                            classes = node.props.class.join(' ');
                        }
                        if (
                            classes.includes('uil') ||
                            classes.includes('mdi') ||
                            classes.includes('fa') ||
                            classes.includes('pi')
                        ) {
                            return true;
                        }
                    }

                    // Recursively check children
                    if (node.children && Array.isArray(node.children)) {
                        if (hasIconInSlot(node.children)) return true;
                    }

                    return false;
                });
            };

            return hasIconInSlot(slots);
        },
        slotIconInfo() {
            if (!this.hasSlotIcon || !this.$slots.default) {
                return null;
            }

            const slots = this.$slots.default();

            const extractIcon = nodes => {
                const nodeArray = Array.isArray(nodes) ? nodes : [nodes];

                for (let i = 0; i < nodeArray.length; i++) {
                    const node = nodeArray[i];
                    if (!node) {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    // Check if it's an icon component
                    if (node.type && typeof node.type === 'object') {
                        const componentName = node.type.name || node.type.__name;
                        if (componentName === 'EIcon' || componentName === 'BIcon') {
                            let iconProp = node.props?.icon || node.props?.class;

                            if (iconProp && !iconProp.includes('pi') && !iconProp.includes('uil')) {
                                iconProp = `uil uil-${iconProp}`;
                            }

                            return { icon: iconProp, position: 'left' };
                        }
                    }

                    if (node.type === 'i' && node.props && node.props.class) {
                        let classes = '';
                        if (typeof node.props.class === 'string') {
                            classes = node.props.class;
                        } else if (Array.isArray(node.props.class)) {
                            classes = node.props.class.join(' ');
                        }
                        return { icon: classes, position: 'left' };
                    }

                    if (node.children && Array.isArray(node.children)) {
                        const result = extractIcon(node.children);
                        if (result) return result;
                    }
                }
                return null;
            };

            return extractIcon(slots);
        },
        slotText() {
            if (!this.hasIcon || !this.$slots.default) {
                return undefined;
            }
            const slots = this.$slots.default();

            const extractText = node => {
                if (typeof node === 'string') {
                    return node;
                }
                if (Array.isArray(node)) {
                    return node.map(extractText).join('');
                }
                if (node && typeof node === 'object') {
                    // Skip icon components and icon elements
                    if (node.type && typeof node.type === 'object') {
                        const componentName = node.type.name || node.type.__name;
                        if (componentName === 'EIcon' || componentName === 'BIcon') {
                            return '';
                        }
                    }
                    if (node.type === 'i') {
                        return '';
                    }

                    // VNode with children
                    if (node.children) {
                        if (typeof node.children === 'string') {
                            return node.children;
                        }
                        if (Array.isArray(node.children)) {
                            return node.children.map(extractText).join('');
                        }
                    }
                    // VNode with text
                    if (node.text) {
                        return node.text;
                    }
                }
                return '';
            };

            return extractText(slots);
        },
    },

    methods: {
        /**
         * Extract severity from class names and filter out type classes
         * @param {string|array|object} classes - The classes to process
         * @returns {object} Object with severity and filteredClasses
         */
        extractSeverityFromClasses(classes) {
            let classArray = [];

            if (typeof classes === 'string') {
                classArray = classes.split(' ');
            } else if (Array.isArray(classes)) {
                classArray = classes;
            } else if (typeof classes === 'object') {
                classArray = Object.keys(classes).filter(key => classes[key]);
            }

            const typeClasses = [
                'is-primary',
                'is-success',
                'is-danger',
                'is-warning',
                'is-info',
                'is-link',
                'is-dark',
                'is-light',
            ];

            let severity = null;
            const filteredClasses = [];

            classArray.forEach(cls => {
                if (typeClasses.includes(cls)) {
                    if (!severity) {
                        severity = getPrimeVueSeverity(cls);
                    }
                } else {
                    filteredClasses.push(cls);
                }
            });

            return {
                severity,
                filteredClasses: filteredClasses.filter(Boolean).join(' '),
            };
        },

        /**
         * Helper function to map Buefy button size to PrimeVue size
         * @param {string} size - The Buefy button size (e.g., 'is-large', 'small')
         * @returns {string|undefined} PrimeVue size value
         */
        getPrimeVueButtonSize(size) {
            return PRIMEVUE_BUTTON_SIZE_MAP[size];
        },
    },
};
</script>

<style lang="scss" scoped>
a {
    font-family: stolzl, sans-serif;
}
</style>

